import React from 'react';
import PropTypes from 'prop-types';
import Icon from 'components/Icon';
import styles from './EmbedCardToolbar.css?module';
import {connect} from 'react-redux';
import cx from 'classnames';
import {CARD_SETTINGS} from 'app/CardConstants';
import {selectIsCardSettingEnabled} from 'state/cardSetting/cardSettingSelectors';
import {analyticsTrack} from 'utilities/analytics';
import {ADOBE_ANALYTICS_EVENTS} from 'app/Analytics';
import ExportModal from 'components/ExportModal';
import {
  selectHasChartPicker,
  selectIsChartPickerActive,
  selectHasTable,
  selectHasFilters,
  selectIsFilterActive,
  selectHasExport,
} from 'state/cardDetail/cardDetailSelectors';
import ButtonUnstyled from 'components/ButtonUnstyled';
import {injectIntl, defineMessages} from 'react-intl';

import {
  toggleChartPicker,
  toggleFilter,
} from 'state/cardDetail/cardDetailActions';

const MESSAGES = defineMessages({
  chart: {
    defaultMessage: 'Change chart type',
    id: 'EmbedCardToolbar__ChartIconLabel',
    description: 'Label for icon that toggles the display of the chart picker',
  },
  table: {
    defaultMessage: 'Show data as a table',
    id: 'EmbedCardToolbar__TableIconLabel',
    description:
      'Label for icon that toggles the display of the data as a table',
  },
  filter: {
    defaultMessage: 'Filter data used in this chart',
    id: 'EmbedCardToolbar__FilterIconLabel',
    description: 'Label for the icon that toggles the filter widget',
  },
  export: {
    defaultMessage: 'Export the data used in this chart',
    id: 'EmbedCardToolbar__ExportIconLabel',
    description: 'Label for the icon that toggles the export modal',
  },
});

const ToolbarItem = ({icon, onClick, active, title}) => (
  <div className={styles.item}>
    <ButtonUnstyled onClick={onClick} title={title}>
      <Icon
        className={cx({
          [styles.active]: active,
        })}
        icon={icon}
        shade={Icon.SHADES.MEDIUM}
        size={Icon.SIZES.SCALABLE}
      />
    </ButtonUnstyled>
  </div>
);

const deactivateExport = () => ({isExportActive: false});
const toggleExport = ({isExportActive}) => ({isExportActive: !isExportActive});

class EmbedCardToolbar extends React.Component {
  state = {isExportActive: false};

  handleChartPickerClick = () => {
    analyticsTrack(ADOBE_ANALYTICS_EVENTS.PUBLIC__CARD__CHART__TOGGLE, {
      card_id: this.props.cardId,
    });
    this.props.toggleChartPicker();
  };

  handleTableClick = () => {
    analyticsTrack(ADOBE_ANALYTICS_EVENTS.PUBLIC__CARD__TABLE__TOGGLE, {
      card_id: this.props.cardId,
    });
    this.props.onTableActionClick();
  };

  handleExportClick = () => this.setState(toggleExport);

  handleRequestExportModalClose = () => this.setState(deactivateExport);

  render() {
    const {
      hasChartPicker,
      isChartPickerEnabled,
      isChartPickerActive,
      hasTable,
      isTableEnabled,
      isTableActive,
      hasFilters,
      isFilterEnabled,
      filters,
      isFilterActive,
      isExportEnabled,
      hasExport,
      cardId,
      intl,
    } = this.props;

    const {isExportActive} = this.state;

    return (
      <div className={styles.toolbar}>
        {isChartPickerEnabled && hasChartPicker ? (
          <ToolbarItem
            icon={Icon.OPTIONS.CHART_PIE}
            active={isChartPickerActive}
            onClick={this.handleChartPickerClick}
            title={intl.formatMessage(MESSAGES.chart)}
          />
        ) : null}
        {isTableEnabled && hasTable ? (
          <ToolbarItem
            icon={Icon.OPTIONS.TABLE}
            active={isTableActive}
            onClick={this.handleTableClick}
            title={intl.formatMessage(MESSAGES.table)}
          />
        ) : null}
        {isFilterEnabled && hasFilters ? (
          <ToolbarItem
            onClick={this.props.toggleFilter}
            icon={Icon.OPTIONS.FUNNEL}
            active={isFilterActive}
            title={intl.formatMessage(MESSAGES.filter)}
          />
        ) : null}
        {isExportEnabled && hasExport ? (
          <ToolbarItem
            onClick={this.handleExportClick}
            icon={Icon.OPTIONS.ARROW_BOX}
            isActive={isExportActive}
            title={intl.formatMessage(MESSAGES.export)}
          />
        ) : null}
        <ExportModal
          isOpen={isExportActive}
          cardId={cardId}
          filters={filters}
          onRequestModalClose={this.handleRequestExportModalClose}
        />
      </div>
    );
  }
}

EmbedCardToolbar.propTypes = {
  cardId: PropTypes.string.isRequired,
  isTableActive: PropTypes.bool.isRequired,
  filters: PropTypes.array.isRequired,
};

export default injectIntl(
  connect(
    (state, {cardId}) => ({
      hasChartPicker: selectHasChartPicker(state, cardId),
      isChartPickerActive: selectIsChartPickerActive(state),
      isChartPickerEnabled: selectIsCardSettingEnabled(
        state,
        CARD_SETTINGS.CHART_PICKER
      ),
      hasTable: selectHasTable(state, cardId),
      isTableEnabled: selectIsCardSettingEnabled(state, CARD_SETTINGS.TABLE),
      hasFilters: selectHasFilters(state, cardId),
      isFilterActive: selectIsFilterActive(state),
      isFilterEnabled: selectIsCardSettingEnabled(state, CARD_SETTINGS.FILTER),
      isExportEnabled: selectIsCardSettingEnabled(state, CARD_SETTINGS.EXPORT),
      hasExport: selectHasExport(state, cardId),
    }),
    {
      toggleChartPicker,
      toggleFilter,
    }
  )(EmbedCardToolbar)
);
